import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import "@/App.css";
import Navigation from "@/components/Navigation";
import { TransitionOverlay } from "@/components/Transition";
import GlobalFooter from "@/components/GlobalFooter";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Businesses from "@/pages/Businesses";
import BusinessDetail from "@/pages/BusinessDetail";
import Capabilities from "@/pages/Capabilities";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";

gsap.registerPlugin(ScrollTrigger);

function Shell() {
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      document.documentElement.classList.add("reduced-motion");
      return undefined;
    }
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    lenis.on("scroll", ScrollTrigger.update);
    const raf = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);
    const onLoad = () => ScrollTrigger.refresh();
    window.addEventListener("load", onLoad);
    return () => {
      window.removeEventListener("load", onLoad);
      gsap.ticker.remove(raf);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen bg-ink text-bone">
      <div className="noise-overlay" aria-hidden="true" />
      <Navigation />
      <TransitionOverlay />
      <Routes location={location}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/businesses" element={<Businesses />} />
        <Route path="/businesses/:slug" element={<BusinessDetail />} />
        <Route path="/capabilities" element={<Capabilities />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about-us" element={<Navigate to="/about" replace />} />
        <Route path="/services" element={<Navigate to="/capabilities" replace />} />
        <Route path="/shipping-services" element={<Navigate to="/capabilities" replace />} />
        <Route path="/software" element={<Navigate to="/capabilities" replace />} />
        <Route path="/projects" element={<Navigate to="/businesses" replace />} />
        <Route path="/projects/*" element={<Navigate to="/businesses" replace />} />
        <Route path="/our-fleet" element={<Navigate to="/businesses/mv-artax" replace />} />
        <Route path="/contact-us" element={<Navigate to="/contact" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!isHome && <GlobalFooter />}
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
